/**
 * BlogPostHeader - Header section for blog posts with title, date, author, category
 */

import { Link } from "react-router-dom";
import { getCategoryBySlug, type CategorySlug } from "../../lib/blog";

interface BlogPostHeaderProps {
  title: string;
  date: string;
  author: string;
  category: CategorySlug;
  tags?: string[];
}

const CATEGORY_GRADIENTS: Record<string, string> = {
  "early-coding": "from-pixel-light/30 via-magic-50 to-white",
  "screen-time": "from-magic-100 via-sunshine-50 to-white",
  "kindergarten-prep": "from-sunshine-100 via-magic-50 to-white",
  "learning-through-play": "from-meadow-100 via-sunshine-50 to-white",
};

export function BlogPostHeader({
  title,
  date,
  author,
  category,
  tags = [],
}: BlogPostHeaderProps) {
  const categoryInfo = getCategoryBySlug(category);
  const gradient = CATEGORY_GRADIENTS[category] || "from-magic-100 via-sunshine-50 to-white";

  // Format date
  const formattedDate = new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return (
    <header className={`py-16 md:py-24 bg-gradient-to-b ${gradient}`}>
      <div className="max-w-3xl mx-auto px-4">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 font-body text-body-sm text-neutral-500">
            <li>
              <Link to="/blog" className="hover:text-magic-600 transition-colors">
                Blog
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link
                to={`/blog/${category}`}
                className="hover:text-magic-600 transition-colors"
              >
                {categoryInfo?.name || category}
              </Link>
            </li>
          </ol>
        </nav>

        {/* Category badge */}
        <Link
          to={`/blog/${category}`}
          className="inline-block px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full
                   font-body text-body-sm font-semibold text-neutral-700
                   hover:bg-magic-100 hover:text-magic-700 transition-colors mb-6"
          style={{ lineHeight: '1.8' }}
        >
          {categoryInfo?.name || category}
        </Link>

        {/* Title */}
        <h1 className="font-display font-extrabold text-display-md md:text-display-lg text-neutral-900 mb-6 text-balance">
          {title}
        </h1>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-4 font-body text-body-md text-neutral-600">
          <span>By {author}</span>
          <span className="text-neutral-300">|</span>
          <time dateTime={date}>{formattedDate}</time>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            {tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 bg-neutral-100 rounded-full font-body text-body-xs text-neutral-600"
                style={{ lineHeight: '1.8' }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}
